import { Component, Input, SimpleChanges, Output, EventEmitter, OnChanges, Inject, LOCALE_ID } from '@angular/core';
import { User } from 'src/classes/user';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import countries from 'src/utils/countries';

@Component({
  selector: 'app-personal-details-form',
  templateUrl: './personal-details-form.component.html',
  styleUrls: ['./personal-details-form.component.scss']
})
export class PersonalDetailsFormComponent implements OnChanges {

  @Input() user: User;
  @Input() isEditState: boolean;
  @Input() isFormDisabled: boolean = false;
  @Input() isFormLoading: boolean;
  @Output() saveRequest = new EventEmitter<{value: string, key: string}[]>();
  @Output() closeRequest = new EventEmitter<void>();

  public editForm: FormGroup;
  public formErrors: string[] = [];
  public countries = countries;

  constructor(
    private formBuilder: FormBuilder,
    @Inject(LOCALE_ID) private locale: string
  ) {
    if (!this.user) {
      this.formErrors.push('Cannot create form: User object is missing');
      return;
    }
  }

  isEnglishLocale() {
    return !(typeof this.locale === 'string' && this.locale.toLowerCase().substring(0, 2) === 'pt');
  }

  setupEditForm() {
    this.formErrors = [];
    let countryName = this.user.paramValues.country;
    let phone = this.user.paramValues.phone;
    const country = countries.find(country => country.name === countryName);
    if (country) {
      if (phone && country.codeNumber && ('+'+phone).startsWith(country.codeNumber)) {
        phone = phone.substring(country.codeNumber.length-1);
      }
    } else {
      countryName = '';
    }
    this.editForm = this.formBuilder.group({
      firstName: [this.user.paramValues.firstName, Validators.required],
      lastName: [this.user.paramValues.lastName, Validators.required],
      company: [this.user.paramValues.company, Validators.required],
      country: [countryName, Validators.required],
      phone: [phone, Validators.required],
      city: [this.user.paramValues.city, Validators.required],
      zipCode: [this.user.paramValues.zipCode, Validators.required]
    });
    this.editForm.enable();
  }

  closeEditForm() {
    this.editForm = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    const isEditState = changes.isEditState;
    if (isEditState && this.user) {
      if (isEditState.currentValue && !this.editForm) {
        this.setupEditForm();
      } else if (!isEditState.currentValue && this.editForm) {
        this.closeEditForm();
      }
    }

    const isFormDisabled = changes.isFormDisabled;
    if (isFormDisabled && this.editForm) {
      if (isFormDisabled.currentValue) {
        this.editForm.disable();
      } else {
        this.editForm.enable();
      }
    }
  }

  onSubmitClick() {
    this.formErrors = [];
    if (this.editForm.invalid) {
      this.formErrors.push(this.isEnglishLocale() ? 'Invalid form. Review your data.' : 'Formulário inválido. Verifique seus dados.');
      this.editForm.markAllAsTouched();
      return;
    }
    try {
      const paramList: {value: string, key: string}[] = [];
      let phone = '';
      if (this.firstName.dirty) { paramList.push({key: 'firstName', value: this.firstName.value}); }
      if (this.lastName.dirty) { paramList.push({key: 'lastName', value: this.lastName.value}); }
      if (this.company.dirty) { paramList.push({key: 'company', value: this.company.value}); }
      if (this.country.dirty) { paramList.push({key: 'country', value: this.country.value}); }
      if (this.country.dirty || this.phone.dirty) {
        const countryName = this.country.value;
        phone = this.phone.value;
        const country = countries.find(country => country.name === countryName);
        if (country && phone && country.codeNumber) {
          phone = country.codeNumber.substring(1) + phone;
        }
        paramList.push({key: 'phone', value: phone});
      }
      if (this.city.dirty) { paramList.push({key: 'city', value: this.city.value}); }
      if (this.zipCode.dirty) { paramList.push({key: 'zipCode', value: this.zipCode.value}); }
      this.saveRequest.emit(paramList);
    } catch (err) {
      this.formErrors = this.formErrors.concat(err.message.split('\n').filter((str: string) => !!str));
      console.log(err);
    }
  }

  onCancelClick() {
    this.closeRequest.emit();
  }

  get firstName(): FormControl { return this.editForm.get('firstName') as FormControl; }
  get lastName(): FormControl { return this.editForm.get('lastName') as FormControl; }
  get company(): FormControl { return this.editForm.get('company') as FormControl; }
  get phone(): FormControl { return this.editForm.get('phone') as FormControl; }
  get country(): FormControl { return this.editForm.get('country') as FormControl; }
  get city(): FormControl { return this.editForm.get('city') as FormControl; }
  get zipCode(): FormControl { return this.editForm.get('zipCode') as FormControl; }

}
